import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerOverlay,
  useColorMode
} from '@chakra-ui/react';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import ProfileAvatar from '../../presentation/my-page/components/ProfileAvatar';
import PulsingGradientCircle from '../../presentation/chat/components/PulsingGradientCircle';
import { InputBoxSingle } from '@peacecover/component';
import EditIconButton from '../../presentation/my-page/components/EditIconButton';
import { useBettieProfileUpdateStore } from '../../presentation/chat/store/useBettieProfileUpdateStore';
import { useFilePicker } from 'use-file-picker';
import type { components } from '../../types/schema';
import BettieProfileImageOverlay from '../../presentation/chat/components/BettieProfileImageOverlay';
import { useUpdateBettieProfile } from '../../domains/bettie/ui/hooks/useUpdateBettieProfile';
import { useQueryClient } from 'react-query';

interface BettieProfileUpdateBottomDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  bettieProfile: components['schemas']['ApiV1BettieProfile'];
  chatRoomId: string;
}

export type ImageStep = 'idle' | 'crop' | 'preview';

const BettieProfileUpdateBottomDrawer = ({
  isOpen,
  onClose,
  bettieProfile,
  chatRoomId
}: BettieProfileUpdateBottomDrawerProps) => {
  const { colorMode } = useColorMode();
  const { editedNickname, setEditedNickname, editedDataUrl, setEditedDataUrl, clear } =
    useBettieProfileUpdateStore();
  const queryClient = useQueryClient();

  const {
    openFilePicker,
    plainFiles,
    clear: clearFilePicker
  } = useFilePicker({
    readAs: 'DataURL',
    accept: 'image/*',
    multiple: false
  });

  const [imageStep, setImageStep] = useState<ImageStep>('idle');
  const [profileImageDraft, setProfileImageDraft] = useState<{
    sourceUrl: string | null;
    croppedDataUrl: string | null;
  }>({ sourceUrl: null, croppedDataUrl: null });
  const [isLoading, setIsLoading] = useState(false);

  const { mutateAsync: updateBettieProfile } = useUpdateBettieProfile();

  const revokeIfBlob = (url: string | null) => {
    if (url?.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  };

  useEffect(() => {
    if (!plainFiles?.[0]) {
      return;
    }
    const file = plainFiles[0];

    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      alert(t('BettieSetting.profileImage.sizeLimitAlert'));
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setProfileImageDraft({ sourceUrl: objectUrl, croppedDataUrl: null });
    setImageStep('crop');
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [plainFiles]);

  const handleCropComplete = (croppedImageUrl: string) => {
    setProfileImageDraft((prev) => ({ ...prev, croppedDataUrl: croppedImageUrl }));
    setImageStep('preview');
  };

  const handleBackFromCrop = () => {
    setImageStep('idle');
    revokeIfBlob(profileImageDraft.sourceUrl);
    setProfileImageDraft({ sourceUrl: null, croppedDataUrl: null });
  };

  const handlePreviewBack = () => {
    // 프리뷰에서 뒤로가면 다시 크롭 화면으로
    setProfileImageDraft((prev) => ({ ...prev, croppedDataUrl: null }));
    setImageStep('crop');
  };

  const handlePreviewConfirm = () => {
    if (profileImageDraft.croppedDataUrl) {
      setEditedDataUrl(profileImageDraft.croppedDataUrl);
    }
    revokeIfBlob(profileImageDraft.sourceUrl);
    setProfileImageDraft({ sourceUrl: null, croppedDataUrl: null });
    setImageStep('idle');
  };

  const handleClose = () => {
    onClose();
    clear();
    clearFilePicker();
    setImageStep('idle');
    revokeIfBlob(profileImageDraft.sourceUrl);
    setProfileImageDraft({ sourceUrl: null, croppedDataUrl: null });
  };

  const handleUpdateProfile = async () => {
    setIsLoading(true);
    const responseBody = {
      chat_room: {
        bettie_nickname: editedNickname,
        data_url: editedDataUrl ?? ''
      }
    };
    try {
      const response = await updateBettieProfile({ body: responseBody, chatRoomId });
      if (response.chatRoom) {
        handleClose();
        queryClient.invalidateQueries(['ChatRoomMessages', chatRoomId]);
        queryClient.invalidateQueries(['bettieChatRoom', chatRoomId]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} placement="bottom">
      <DrawerOverlay />
      <DrawerContent
        motionProps={{
          variants: {
            enter: {
              y: '0%',
              transition: { duration: 0.15 }
            },
            exit: {
              y: '100%',
              transition: { duration: 0.1 }
            }
          }
        }}
        style={{
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          boxShadow:
            colorMode === 'dark'
              ? '0 -6px 7.5px 0 rgba(0, 0, 0, 0.65)'
              : '0 -5px 8.8px 0 rgba(0, 0, 0, 0.07)'
        }}
      >
        <DrawerBody paddingTop="40px" paddingBottom="30px" paddingX="24px">
          <BettieProfileImageOverlay
            imageStep={imageStep}
            selectedImageForCrop={profileImageDraft.sourceUrl}
            previewImage={profileImageDraft.croppedDataUrl}
            onCropComplete={handleCropComplete}
            onBackFromCrop={handleBackFromCrop}
            onPreviewBack={handlePreviewBack}
            onPreviewConfirm={handlePreviewConfirm}
          />
          {imageStep === 'idle' ? (
            <div className="flex flex-col items-center gap-[27px]">
              <div className="flex flex-col items-center gap-[8px]">
                <p className="text-[17px] font-bold text-pure-black dark:text-pure-white leading-[20px]">
                  {t('BettieChatRoom.profileUpdateDrawer.title')}
                </p>
                <p className="text-[15px] font-medium text-text-main-lv4 leading-[18px] text-center whitespace-pre-line">
                  {t('BettieChatRoom.profileUpdateDrawer.description')}
                </p>
              </div>
              <div className="flex flex-col items-center gap-[20px] w-full">
                <PulsingGradientCircle
                  gradientCircleSize="70px"
                  glassCircleSize="70px"
                  showDottedCircle={false}
                  blur="4.7px"
                >
                  <button type="button" className="relative" onClick={openFilePicker}>
                    <ProfileAvatar
                      thumbnailUrl={editedDataUrl ?? bettieProfile.thumbnailUrl}
                      widthAndHeight="64px"
                      svgWidthAndHeight="64px"
                    />
                    <EditIconButton className="absolute bottom-0 right-0 border-[2px] border-pure-white dark:border-backgroundElevated-dark cursor-pointer" />
                  </button>
                </PulsingGradientCircle>
                <InputBoxSingle
                  className="w-full bg-transparent"
                  showIcon={true}
                  value={editedNickname}
                  onChange={(e) => setEditedNickname(e.target.value)}
                  onClear={() => setEditedNickname('')}
                  maxLength={20}
                  placeholder={
                    bettieProfile.nickname ?? t('BettieSetting.bettieUserNickname.placeholder')
                  }
                />
                <div className="mt-[-9px]">
                  <ul className="text-[13px] leading-[20px] text-text-main-lv4 list-disc list-inside">
                    <li>{t('BettieChatRoom.profileUpdateDrawer.remainCharacter')}</li>
                    <li>{t('BettieChatRoom.profileUpdateDrawer.changeChatRoom')}</li>
                  </ul>
                </div>
              </div>
            </div>
          ) : null}
        </DrawerBody>
        <DrawerFooter p={0}>
          <button
            type="button"
            className="bg-primary-light dark:bg-primary-dark w-full pt-[14px] pb-[calc(env(safe-area-inset-bottom)+14px)]"
            disabled={isLoading}
            onClick={handleUpdateProfile}
          >
            <p className="text-center text-on-primary text-[17px] font-semibold leading-[20px]">
              {t('done')}
            </p>
          </button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default BettieProfileUpdateBottomDrawer;
